// ─── One way of showing dates, day counts, roles and holiday wording across
// the app (UK / South African style: "6 Nov 2026", "12–16 Nov 2026").
import { format, parseISO, isValid } from 'date-fns';

const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isValid(v) ? v : null;
  try {
    const d = parseISO(String(v).slice(0, 10));
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
};

/** "6 Nov 2026" */
export function fmtDate(v, { weekday = false, year = true } = {}) {
  const d = toDate(v);
  if (!d) return '';
  return format(d, `${weekday ? 'EEE ' : ''}d MMM${year ? ' yyyy' : ''}`);
}

/** "Fri 6 Nov 2026", "12–16 Nov 2026", "28 Sep – 2 Oct 2026", "30 Dec 2026 – 2 Jan 2027" */
export function fmtRange(fromV, toV, { weekday = true } = {}) {
  const from = toDate(fromV);
  const to = toDate(toV || fromV) || from;
  if (!from) return '';
  if (+from === +to) return format(from, `${weekday ? 'EEE ' : ''}d MMM yyyy`);
  if (from.getFullYear() !== to.getFullYear()) return `${format(from, 'd MMM yyyy')} – ${format(to, 'd MMM yyyy')}`;
  if (from.getMonth() === to.getMonth()) return `${format(from, 'd')}–${format(to, 'd MMM yyyy')}`;
  return `${format(from, 'd MMM')} – ${format(to, 'd MMM yyyy')}`;
}

/** 4 → "4 days", 0.5 → "½ day", 2.5 → "2½ days" */
export function fmtDayCount(n, { short = false } = {}) {
  const v = Number(n) || 0;
  const sign = v < 0 ? '−' : '';
  const a = Math.abs(v);
  const whole = Math.floor(a);
  const half = Math.abs(a - whole - 0.5) < 0.01;
  const num = half ? `${whole > 0 ? whole : ''}½` : `${Math.round(a * 10) / 10}`;
  if (short) return `${sign}${num}d`;
  return `${sign}${num} ${a === 1 || (half && whole === 0) ? 'day' : 'days'}`;
}

export function fmtHourCount(n) {
  const v = Math.round((Number(n) || 0) * 10) / 10;
  return `${v < 0 ? '−' : ''}${Math.abs(v)}h`;
}

/** A balance figure in its unit */
export function fmtAmount(n, unit, opts) {
  return unit === 'hours' ? fmtHourCount(n) : fmtDayCount(n, opts);
}

/** "Bank holiday" in the UK, "Public holiday" everywhere else (ZA, AU). */
export function holidayTerm(market, { plural = false, lower = false } = {}) {
  const base = String(market || '').toUpperCase() === 'GB' ? 'Bank holiday' : 'Public holiday';
  const word = plural ? `${base}s` : base;
  return lower ? word.toLowerCase() : word;
}

// ─── One role vocabulary everywhere: Super admin, Admin, Manager, Employee ───
export const ROLE_LABELS = {
  super_admin: 'Super admin',
  admin: 'Admin',
  manager: 'Manager',
  member: 'Employee',
};

/** A person's role as shown in the app. Department admins are "Manager". */
export function roleLabelFor(role, { isDepartmentAdmin = false } = {}) {
  if (role === 'member' && isDepartmentAdmin) return ROLE_LABELS.manager;
  return ROLE_LABELS[role] ?? ROLE_LABELS.member;
}